// Support Ticket Store - Simulated persistence with module-level array

export interface SupportTicket {
  id: string
  type: 'complaint' | 'terminal-request' | 'device-issue'
  category: string
  description: string
  merchantId: string
  status: 'Open' | 'Under Review' | 'Resolved' | 'Closed'
  priority: 'Low' | 'Medium' | 'High' | 'Critical'
  createdDate: string
  lastUpdated: string
  supportTeam: string
  details: Record<string, string>
  expectedResolution?: string
}

// Module-level storage (simulated persistence)
let tickets: SupportTicket[] = [
  {
    id: 'CMP-2026-000231',
    type: 'complaint',
    category: 'Transaction Failed',
    description: 'Customer payment was declined but amount was debited',
    merchantId: '050102100023650',
    status: 'Under Review',
    priority: 'High',
    createdDate: '2026-03-05 10:30:00',
    lastUpdated: '2026-03-06 14:20:00',
    supportTeam: 'Transaction Support',
    details: {
      transactionId: 'TX-982374',
      amount: '4500',
      channel: 'POS',
    },
    expectedResolution: '2026-03-08',
  },
  {
    id: 'TERM-REQ-2026-000195',
    type: 'terminal-request',
    category: 'New Terminal',
    description: 'Request for new POS terminal for DHA branch expansion',
    merchantId: '050102100023650',
    status: 'Open',
    priority: 'Medium',
    createdDate: '2026-03-04 09:15:00',
    lastUpdated: '2026-03-04 09:15:00',
    supportTeam: 'Onboarding Team',
    details: {
      terminalType: 'POS Terminal',
      quantity: '2',
      branch: 'DHA Outlet',
      reason: 'Business expansion',
    },
    expectedResolution: '2026-03-12',
  },
  {
    id: 'DEV-ISS-2026-000084',
    type: 'device-issue',
    category: 'Printer malfunction',
    description: 'Receipt printer not working on terminal T-8472',
    merchantId: '050102100023650',
    status: 'Resolved',
    priority: 'Low',
    createdDate: '2026-03-01 16:45:00',
    lastUpdated: '2026-03-03 11:30:00',
    supportTeam: 'Technical Support',
    details: {
      terminalId: 'T-8472',
      terminalType: 'POS',
      errorMessage: 'Printer offline',
    },
    expectedResolution: '2026-03-03',
  },
]

// Counter for generating unique IDs
let complaintCounter = 234
let terminalRequestCounter = 199
let deviceIssueCounter = 88

export const getTickets = (): SupportTicket[] => {
  return [...tickets]
}

export const getTicketsByMerchant = (merchantId: string): SupportTicket[] => {
  return tickets.filter((t) => t.merchantId === merchantId)
}

export const getTicketById = (id: string): SupportTicket | undefined => {
  return tickets.find((t) => t.id === id)
}

export const addTicket = (
  ticket: Omit<
    SupportTicket,
    | 'id'
    | 'createdDate'
    | 'lastUpdated'
    | 'status'
    | 'supportTeam'
    | 'expectedResolution'
  >,
): SupportTicket => {
  let id: string
  let supportTeam: string

  const now = new Date()
  const dateStr =
    now.toISOString().slice(0, 10).replace(/-/g, '-') +
    ' ' +
    now.toTimeString().slice(0, 8)

  // Calculate expected resolution (3-5 business days)
  const resolutionDate = new Date(now)
  resolutionDate.setDate(
    resolutionDate.getDate() +
      (ticket.priority === 'Critical' ? 1 : ticket.priority === 'High' ? 3 : 5),
  )
  const expectedResolution = resolutionDate.toISOString().slice(0, 10)

  switch (ticket.type) {
    case 'complaint':
      complaintCounter++
      id = `CMP-2026-${String(complaintCounter).padStart(6, '0')}`
      supportTeam = 'Transaction Support'
      break
    case 'terminal-request':
      terminalRequestCounter++
      id = `TERM-REQ-2026-${String(terminalRequestCounter).padStart(6, '0')}`
      supportTeam = 'Onboarding Team'
      break
    case 'device-issue':
      deviceIssueCounter++
      id = `DEV-ISS-2026-${String(deviceIssueCounter).padStart(6, '0')}`
      supportTeam = 'Technical Support'
      break
    default:
      id = `TKT-${Date.now()}`
      supportTeam = 'General Support'
  }

  const newTicket: SupportTicket = {
    ...ticket,
    id,
    status: 'Open',
    createdDate: dateStr,
    lastUpdated: dateStr,
    supportTeam,
    expectedResolution,
  }

  tickets = [newTicket, ...tickets]
  return newTicket
}

export const updateTicketStatus = (
  id: string,
  status: SupportTicket['status'],
): SupportTicket | undefined => {
  const index = tickets.findIndex((t) => t.id === id)
  if (index === -1) return undefined

  const now = new Date()
  const dateStr =
    now.toISOString().slice(0, 10).replace(/-/g, '-') +
    ' ' +
    now.toTimeString().slice(0, 8)

  tickets[index] = {
    ...tickets[index],
    status,
    lastUpdated: dateStr,
  }

  return tickets[index]
}

// AI Knowledge Base for common questions
export const aiKnowledgeBase: Record<string, string> = {
  'transaction fail': `Common reasons for transaction failures include:
• Insufficient funds in customer account
• Card expired or blocked
• Network connectivity issues
• Incorrect PIN entered multiple times
• Daily transaction limit exceeded

If the issue persists, please create a support ticket for our team to investigate.`,

  'settlement report': `To download settlement reports:
1. Go to Dashboard → Transaction History
2. Click on "Export" button in the top right
3. Select date range and format (PDF/Excel)
4. Click "Download"

Settlement reports are also emailed daily to your registered email address.`,

  'reset password': `To reset your Merchant Portal password:
1. Click "Forgot Password" on the login page
2. Enter your registered email address
3. Check your email for the reset link
4. Create a new password (min 8 characters, include numbers and special characters)

For security, the reset link expires in 30 minutes.`,

  refund: `To process a refund:
1. Go to Transaction History
2. Find the transaction and click "View"
3. Click "Process Refund" button
4. Enter refund amount (full or partial)
5. Confirm the refund

Refunds are processed within 3-5 business days. For refunds older than 30 days, please create a support ticket.`,

  'terminal offline': `If your terminal shows offline:
1. Check power connection
2. Verify network/SIM connectivity
3. Restart the terminal
4. Check if other devices on same network work

If the issue persists after these steps, please report a device issue through this chat.`,

  chargeback: `For chargeback disputes:
1. Gather transaction evidence (receipts, delivery proof)
2. Create a support ticket with all documentation
3. Our team will review within 24 hours
4. You'll receive guidance on the dispute process

Time-sensitive: Chargebacks must be responded to within 7 days.`,
}

export const getAIResponse = (query: string): string | null => {
  const lowerQuery = query.toLowerCase()

  for (const [key, response] of Object.entries(aiKnowledgeBase)) {
    if (lowerQuery.includes(key)) {
      return response
    }
  }

  // Check for common keywords
  if (
    lowerQuery.includes('fail') ||
    lowerQuery.includes('decline') ||
    lowerQuery.includes('reject')
  ) {
    return aiKnowledgeBase['transaction fail']
  }
  if (
    lowerQuery.includes('report') ||
    lowerQuery.includes('download') ||
    lowerQuery.includes('export')
  ) {
    return aiKnowledgeBase['settlement report']
  }
  if (
    lowerQuery.includes('password') ||
    lowerQuery.includes('login') ||
    lowerQuery.includes('access')
  ) {
    return aiKnowledgeBase['reset password']
  }
  if (
    lowerQuery.includes('refund') ||
    lowerQuery.includes('return') ||
    lowerQuery.includes('money back')
  ) {
    return aiKnowledgeBase['refund']
  }
  if (
    lowerQuery.includes('offline') ||
    lowerQuery.includes('connect') ||
    lowerQuery.includes('network')
  ) {
    return aiKnowledgeBase['terminal offline']
  }
  if (lowerQuery.includes('chargeback') || lowerQuery.includes('dispute')) {
    return aiKnowledgeBase['chargeback']
  }

  return null
}
